// Copyright (c) Docugami, Inc. All rights reserved.

import { DocExplorerNavigationItemLabel } from 'components/common/Fonts'
import { appContentColor, disabledGreyText, greyText, primaryTheme, themeWhite } from 'components/shell/presentation/AppStyles'
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react"
import styled from 'styled-components'
import { Badge, IBadgeProps } from './Badge'
import { BasicIconProps, IconRenderer } from 'components/work_path/IconRenderer'
import { LightDisabledCss } from 'components/common/DgCommonStyledComponents'

export enum NavigationItemClassNames {
    navigationItemButton = "navigationButton"
}

const NavigationItemContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 5px 7px 3px 15px;
    box-sizing: border-box;
    gap: 7px;
`;

const NavigationItemContainer = styled.div.attrs(props => ({ className: props.className }))<{ isSelected: boolean, disabled?: boolean }>`
    display: flex;
    border-radius: 5px;
    color: ${props => props.isSelected ? themeWhite : ""};
    background: ${props => props.isSelected ? greyText : ""};
    height: 30px;
    width: 100%;
    box-sizing: border-box;

    &.navigationButton {
        justify-content: center;
        color: ${props => props.isSelected ? themeWhite : appContentColor};
        ${props => !props.isSelected && `background-color: ${primaryTheme.palette.whiteTranslucent40}`};
        ${NavigationItemContentContainer} {
            width: fit-content;
            max-width: 100%;
        }
    }

    :hover {
        background: ${props => !props.isSelected ? themeWhite : ""};
        cursor: pointer;
    }

    ${props => !!props.disabled && LightDisabledCss};
`;

const NavigationItemLabelContainer = styled.div`
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const GreyIconContainer = styled.div<{ isSelected: boolean }>`
    path {
        fill: ${props => `${props.isSelected ? themeWhite : greyText} !important`};
    }
`;

export interface NavigationItemProps {
    title: string;
    isSelected: boolean;
    onSelected: () => void;
    badgeProps?: IBadgeProps;
    icon?: BasicIconProps;
    disabled?: boolean;
    automationId: string;
    styles?: NavigationItemClassNames[];
}

export const NavigationItem = (props: NavigationItemProps): JSX.Element => {

    function getIcon(): JSX.Element | null {
        if (!props.icon) {
            return null;
        }

        const iconInfo = {
            name: props.icon.name,
            height: props.icon.height,
            width: props.icon.width,
            defaultColor: props.isSelected ? disabledGreyText : greyText
        }

        return (
            <GreyIconContainer isSelected={props.isSelected}>
                <IconRenderer source={iconInfo} height={props.icon.height} width={props.icon.width} containerHeight={props.icon.height} containerWidth={props.icon.width} />
            </GreyIconContainer>
        );
    }

    function onSelected() {
        if (!props.isSelected) {
            props.onSelected();
        }
    }

    return (
        <NavigationItemContainer
            className={props.styles?.join(' ')}
            data-automation-id={props.automationId}
            isSelected={props.isSelected}
            onClick={() => onSelected()}
            disabled={props.disabled}>
            <NavigationItemContentContainer>
                {getIcon()}
                <NavigationItemLabelContainer>
                    <TooltipHost content={props.title} overflowMode={TooltipOverflowMode.Parent}>
                        <DocExplorerNavigationItemLabel>{props.title}</DocExplorerNavigationItemLabel>
                    </TooltipHost>
                </NavigationItemLabelContainer>
                {!!props.badgeProps && <Badge {...props.badgeProps} />}
            </NavigationItemContentContainer>
        </NavigationItemContainer>
    )
}
